// =====================================================================================
// INVRT Radar — web console — account — delivery — shared badges
//
// The state and status vocabularies of §6 and §7, mapped once onto the house Badge tones
// so a webhook delivery and a target attempt in the same state look the same. Tones:
// pending = info, delivered = success (Compound Teal), failed = warning, dead = danger.
// =====================================================================================

import { Badge } from "@/components/ui/badge";
import type {
  DeliveryAttemptState,
  DeliveryTargetStatus,
  WebhookDeliveryState,
  WebhookEndpointStatus,
} from "@/lib/types.c1";
import { eventKindLabel } from "./hooks";

type Tone = "neutral" | "info" | "success" | "warning" | "danger";

const STATE_BADGES: Record<WebhookDeliveryState | DeliveryAttemptState, { tone: Tone; label: string }> = {
  pending: { tone: "info", label: "Pending" },
  delivered: { tone: "success", label: "Delivered" },
  failed: { tone: "warning", label: "Failed" },
  dead: { tone: "danger", label: "Dead" },
};

export function DeliveryStateBadge({ state }: { state: WebhookDeliveryState | DeliveryAttemptState }) {
  const badge = STATE_BADGES[state] ?? { tone: "neutral" as Tone, label: state };
  return <Badge tone={badge.tone}>{badge.label}</Badge>;
}

const TARGET_STATUS_BADGES: Record<DeliveryTargetStatus, { tone: Tone; label: string }> = {
  active: { tone: "success", label: "Active" },
  paused: { tone: "neutral", label: "Paused" },
  // Set by an operator, never by the tenant (the PATCH schema refuses it): the target is
  // dead-lettering until it is resumed.
  disabled: { tone: "danger", label: "Disabled" },
};

export function TargetStatusBadge({ status }: { status: DeliveryTargetStatus }) {
  const badge = TARGET_STATUS_BADGES[status] ?? { tone: "neutral" as Tone, label: status };
  return <Badge tone={badge.tone}>{badge.label}</Badge>;
}

/** A webhook's `status` is `active` | `disabled`; the console says "Paused" for the latter, since a tenant pauses and resumes it themselves. */
export function WebhookStatusBadge({ status }: { status: WebhookEndpointStatus }) {
  return status === "active" ? (
    <Badge tone="success">Active</Badge>
  ) : (
    <Badge tone="neutral">Paused</Badge>
  );
}

export function EventKindBadges({ events }: { events: readonly string[] }) {
  if (events.length === 0) return <span className="text-ink-muted">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {events.map((kind) => (
        <Badge key={kind} tone="info">
          {eventKindLabel(kind)}
        </Badge>
      ))}
    </span>
  );
}
